/**
 * Announcement data transformer — one raw announcement list → canonical
 * news-card items, partitioned across the three landing surfaces.
 *
 * The cutoffs are intentionally pulled out of the components here ("will be
 * changed later"):
 *   Updates ticker      → latest TICKER_LIMIT       (RewampUpdates)
 *   Notification tab    → latest NOTIFICATION_LIMIT  (RewampNewsAndFAQSection)
 *   Archived tab        → everything after NOTIFICATION_LIMIT
 *
 * The /announcement/v1/_search endpoint already returns ACTIVE rows
 * newest-first (`ORDER BY createdtime DESC`) and exposes no limit/offset, so the
 * whole list comes down once (shared react-query cache key) and is sliced on the
 * client. The defensive re-sort below guards against a backend ordering change.
 */

// Latest-N windows. Surfaces overlap by design (the latest 10 appear in both
// the ticker and the notification list).
export const TICKER_LIMIT = 10;
export const NOTIFICATION_LIMIT = 15;

const localeFor = (language) =>
  language === "en_IN" || language === "en" ? "en_IN" : "hi_IN";

const intlLocaleFor = (locale) => (locale === "hi_IN" ? "hi-IN" : "en-US");

// Pick the localized text for `locale`, falling back to English then the first
// available entry so a card never renders blank when one locale is missing.
const pickText = (announcementTexts, locale) => {
  const texts = Array.isArray(announcementTexts) ? announcementTexts : [];
  const match =
    texts.find((tx) => tx?.locale === locale) ??
    texts.find((tx) => tx?.locale === "en_IN") ??
    texts[0];
  return {
    title: match?.title ?? "",
    summary: match?.announcement ?? "",
  };
};

/**
 * Raw API announcements → flat, language-resolved items consumed by
 * RewampNewsCard. Filters to ACTIVE, drops empty entries, and sorts newest
 * first. Text/date are resolved to plain strings for the active language (the
 * card's `loc()` passes plain strings through unchanged).
 */
export const normalizeAnnouncements = (rawList, language) => {
  if (!Array.isArray(rawList)) return [];
  const locale = localeFor(language);
  const intlLocale = intlLocaleFor(locale);

  return rawList
    .filter((a) => a?.state === "ACTIVE")
    .map((a) => {
      const createdTime = a?.auditDetails?.createdTime ?? 0;
      const date = new Date(createdTime);
      const valid = !Number.isNaN(date.getTime()) && createdTime > 0;
      const { title, summary } = pickText(a?.announcementTexts, locale);
      return {
        id: a?.id,
        title,
        summary,
        url: a?.url || "",
        createdTime,
        dateDay: valid ? String(date.getDate()) : "",
        dateMonth: valid
          ? date.toLocaleString(intlLocale, { month: "short" })
          : "",
      };
    })
    .filter((item) => item.title || item.summary)
    .sort((a, b) => b.createdTime - a.createdTime);
};

/**
 * Split a normalized list into the notification (latest N) and archived (rest)
 * windows. Single swappable place for the cutoff.
 */
export const partitionAnnouncements = (list) => {
  const items = Array.isArray(list) ? list : [];
  return {
    notification: items.slice(0, NOTIFICATION_LIMIT),
    archived: items.slice(NOTIFICATION_LIMIT),
  };
};
